import { Box, Button, Code, Em, Flex, Table, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { lightFormat, parseISO } from 'date-fns';
import { t } from 'i18next';
import { ApiType, UserRole } from 'lib_ts/enums/auth.enums';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IApiKey } from 'lib_ts/interfaces/api/key';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CustomerApiService } from 'services/admin/customer-api.service';
import { useAuthStore } from 'stores/use-auth-store';

export const ApiKeysManagement = () => {
  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const init = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [keys, setKeys] = useState<IApiKey[]>([]);
  const [selectedKey, setSelectedKey] = useState<IApiKey | undefined>(
    undefined
  );
  const [dialogRevoke, setDialogRevoke] = useState<number>();

  useEffect(() => {
    if (init.current) {
      return;
    }

    init.current = true;

    CustomerApiService.getInstance()
      .getKeys()
      .then((responseKeys) => {
        setLoaded(true);
        setKeys(responseKeys);
      });
  }, []);

  const generateKey = useCallback(
    (type: ApiType) => {
      CustomerApiService.getInstance()
        .generateKey({
          _parent_id: current.teamID,
          type: type,
        })
        .then((result) => {
          if (!result) {
            return;
          }

          NotifyHelper.success({
            message_md: [
              'Your key has been generated successfully.',
              'Please record it now as it will not be accessible in full later.',
            ].join('\n'),
            buttons: [
              {
                label: 'Copy to Clipboard',
                onClick: () => {
                  navigator.clipboard.writeText(result.key);

                  NotifyHelper.success({
                    message_md: t('common.x-copied-to-clipboard', {
                      x: 'API key',
                    }),
                  });
                },
              },
            ],
            delay_ms: 0,
          });

          setKeys((prevKeys) => [...prevKeys, result]);
        });
    },
    [current.teamID]
  );

  return (
    <ErrorBoundary identifier="ApiKeysManagement">
      <Flex direction="column" gap={RADIX.FLEX.GAP.SECTION} height="100%">
        <Flex gap="2" align="center">
          <Box>
            <Button
              color={RADIX.COLOR.SUCCESS}
              disabled={!loaded}
              onClick={() => {
                if (
                  current.role !== UserRole.admin && // ignore the single key limit for super-admins
                  keys.findIndex(
                    (k) => k.type === ApiType.Customer && !k.disabled
                  ) !== -1
                ) {
                  NotifyHelper.warning({
                    message_md:
                      `You already have an active API key (type: ${ApiType.Customer}). ` +
                      `Please revoke existing keys before requesting a new key of the same type.`,
                  });
                  return;
                }

                generateKey(ApiType.Customer);
              }}
            >
              Generate API Key
            </Button>
          </Box>

          <Text>
            <Em>Do not share these keys with anyone.</Em>
          </Text>

          <Box flexGrow="1">{/* placeholder */}</Box>

          {userRoleFeatureFlags.adminMisc.genCRUDKey && (
            <Box>
              <Button
                title="This feature is only for super-admins"
                color="pink"
                disabled={!loaded}
                onClick={() => {
                  generateKey(ApiType.CRUD);
                }}
              >
                Generate CRUD Key
              </Button>
            </Box>
          )}
        </Flex>

        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Key</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="center">
                Actions
              </Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {keys.map((k, i) => (
              <Table.Row key={`api-key-${i}`}>
                <Table.Cell>
                  <Code color={RADIX.COLOR.NEUTRAL}>{k.key}</Code>
                </Table.Cell>
                <Table.Cell className="capitalize">{k.type}</Table.Cell>
                <Table.Cell>
                  {lightFormat(parseISO(k._created), LOCAL_DATE_FORMAT)}
                </Table.Cell>
                <Table.Cell align="center">
                  <Button
                    variant={RADIX.BUTTON.VARIANT.SECONDARY}
                    color={RADIX.COLOR.DANGER}
                    size={RADIX.BUTTON.SIZE.SM}
                    onClick={() => {
                      setSelectedKey(k);
                      setDialogRevoke(Date.now());
                    }}
                  >
                    Revoke
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}

            {keys.length === 0 && (
              <Table.Row>
                <Table.Cell>{t('common.no-data')}</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </Flex>

      {dialogRevoke && selectedKey && (
        <CommonConfirmationDialog
          key={dialogRevoke}
          identifier="ConfirmRevokeApiKey"
          maxWidth={RADIX.DIALOG.WIDTH.MD}
          title="Revoke Key"
          content={
            <Text>
              This will revoke API access using <Code>{selectedKey.key}</Code>{' '}
              and cannot be undone. You will need to request a new key to
              restore access. Are you sure you want to proceed?
            </Text>
          }
          action={{
            label: 'Revoke',
            color: RADIX.COLOR.DANGER,
            onClick: () => {
              CustomerApiService.getInstance()
                .revokeKey(selectedKey)
                .then((success) => {
                  if (success) {
                    setKeys((prevKeys) =>
                      prevKeys.filter((k) => k._id !== selectedKey._id)
                    );
                  }
                });
            },
          }}
        />
      )}
    </ErrorBoundary>
  );
};
